import React, { useState, useEffect } from 'react';
import { Form, Input, Space, Tag, Typography, Card, message, Alert, Modal, Checkbox } from 'antd';
import { SaveOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const DEFAULT_PCCC_PROMPT = `You are parsing a payroll Excel file for a Chinese construction company (PCCC) operating in Serbia.

**Context:**
- Company: {{company}}
- Project: {{projectCode}}
- Period: {{month}}/{{year}}

**Excel File Structure:**
{{excelStructure}}

**Your Task:**
Parse this Excel file and extract employee payroll data. The file may be in one of these formats:
1. Multi-employee timesheet (hours worked per day)
2. Net salary summary (employee names and net salaries)
3. Wage notification (detailed salary breakdown)
4. Attendance sheet (presence/absence tracking)
5. Commuting expenses

**Output Format (JSON):**
Return ONLY valid JSON in this exact structure:

{
  "fileType": "MULTI_EMPLOYEE_TIMESHEET" | "NET_SALARY_SUMMARY" | "WAGE_NOTIFICATION" | "ATTENDANCE_SHEET" | "COMMUTING_EXPENSES" | "OTHER",
  "confidence": 85,
  "warnings": ["Any parsing warnings or ambiguities"],
  "employees": [
    {
      "name": "Employee full name",
      "surname": "Extracted if separate, otherwise empty",
      "hoursWorked": 176,
      "netSalary": 85000,
      "mealAllowance": 0,
      "regres": 0,
      "vacationDays": 0,
      "sickLeaveDays": 0,
      "workedOnHoliday": false,
      "holidayDates": [],
      "notes": "Any relevant notes from the file"
    }
  ]
}

**Important Rules:**
1. Return ONLY the JSON object, no explanation or markdown
2. Extract all employees found in the file
3. For timesheet files: sum total hours worked across all days
4. For salary files: extract net salary amounts
5. If hours or salary is missing, set to 0
6. Set confidence score (0-100) based on data clarity
7. Add warnings for ambiguous or unclear data
8. Parse both Serbian and Chinese employee names
9. Handle merged cells and multi-sheet structures
10. If employee name is split across columns, combine them

**Begin parsing now:**`;

export default function PromptEditorTab({ config, type }) {
  const { t } = useTranslation(['tables', 'forms']);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isDirty, setIsDirty] = useState(false);
  const [newVersionModalVisible, setNewVersionModalVisible] = useState(false);
  const [copyReferenceFiles, setCopyReferenceFiles] = useState(true);
  const [copyModelSettings, setCopyModelSettings] = useState(true);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Load config data into form
  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        name: config.name,
        description: config.description || '',
        systemPrompt: config.systemPrompt || DEFAULT_PCCC_PROMPT,
      });
      setIsDirty(false);
    }
  }, [config, form]);

  // Create new configuration mutation
  const createMutation = useMutation({
    mutationFn: (data) => API.agentConfig.createAgentConfiguration(data),
    onSuccess: () => {
      message.success(pa('configCreated'));
      queryClient.invalidateQueries(['agent-configs']);
      queryClient.invalidateQueries(['active-agent-config']);
      setIsDirty(false);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('createFailed'));
    },
  });

  // Update configuration mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => API.agentConfig.updateAgentConfiguration(id, data),
    onSuccess: () => {
      message.success(pa('configUpdated'));
      queryClient.invalidateQueries(['agent-configs']);
      queryClient.invalidateQueries(['agent-config-detail']);
      setIsDirty(false);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('updateFailed'));
    },
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        type,
        name: values.name,
        description: values.description,
        systemPrompt: values.systemPrompt,
      };

      if (config) {
        // Update existing
        updateMutation.mutate({
          id: config._id,
          data: {
            ...data,
            changes: 'Prompt updated via UI',
          },
        });
      } else {
        // Create new
        createMutation.mutate(data);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCreateNewVersion = () => {
    setNewVersionModalVisible(true);
  };

  const handleConfirmNewVersion = async () => {
    const values = form.getFieldsValue();
    const data = {
      type,
      name: `${type} v${(config?.version || 0) + 1}`,
      description: values.description,
      systemPrompt: values.systemPrompt,
    };

    // Add model settings if user wants to copy them
    if (copyModelSettings && config) {
      data.modelVersion = config.modelVersion;
      data.temperature = config.temperature;
      data.maxTokens = config.maxTokens;
      data.timeout = config.timeout;
    }

    // If user wants to copy reference files, use clone endpoint then update prompt
    if (copyReferenceFiles && config) {
      try {
        // Clone configuration (copies reference files and settings)
        const cloneResult = await API.agentConfig.cloneConfiguration(config._id, {
          name: data.name,
          description: data.description,
        });

        const newConfigId = cloneResult.data._id;

        // Update the cloned config with current prompt from editor
        await API.agentConfig.updateAgentConfiguration(newConfigId, {
          systemPrompt: values.systemPrompt,
          changes: 'Updated prompt from editor',
        });

        message.success(pa('configCreated'));
        queryClient.invalidateQueries(['agent-configs']);
        queryClient.invalidateQueries(['active-agent-config']);
        setIsDirty(false);
        setNewVersionModalVisible(false);
      } catch (error) {
        message.error(error.response?.data?.message || pa('createFailed'));
      }
    } else {
      createMutation.mutate(data);
      setNewVersionModalVisible(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    const currentPrompt = form.getFieldValue('systemPrompt') || '';
    form.setFieldsValue({
      systemPrompt: currentPrompt + placeholder,
    });
    setIsDirty(true);
  };

  const availablePlaceholders = [
    { key: '{{company}}', desc: pa('companyPlaceholder') },
    { key: '{{projectCode}}', desc: pa('projectCodePlaceholder') },
    { key: '{{month}}', desc: pa('monthPlaceholder') },
    { key: '{{year}}', desc: pa('yearPlaceholder') },
    { key: '{{excelStructure}}', desc: pa('excelStructurePlaceholder') },
    { key: '{{fileCount}}', desc: pa('fileCountPlaceholder') },
  ];

  return (
    <div>
      {config?.active && (
        <Alert
          message={pa('activeConfigWarning')}
          description={pa('activeConfigWarningDesc')}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => setIsDirty(true)}
      >
        <Card style={{ marginBottom: 16 }}>
          <Form.Item
            name="name"
            label={pa('configurationName')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Input placeholder={`${type} v1`} />
          </Form.Item>

          <Form.Item
            name="description"
            label={pa('description')}
          >
            <TextArea rows={2} placeholder={pa('descriptionPlaceholder')} />
          </Form.Item>
        </Card>

        <Card title={pa('systemPrompt')} style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong>{pa('availablePlaceholders')}:</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap>
                {availablePlaceholders.map((placeholder) => (
                  <Tag
                    key={placeholder.key}
                    color="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => insertPlaceholder(placeholder.key)}
                  >
                    <CopyOutlined /> {placeholder.key}
                  </Tag>
                ))}
              </Space>
            </div>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              {pa('clickToInsertPlaceholder')}
            </Paragraph>
          </div>

          <Form.Item
            name="systemPrompt"
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <TextArea
              rows={25}
              placeholder={pa('enterSystemPrompt')}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Alert
            message={pa('promptTips')}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>{pa('promptTip1')}</li>
                <li>{pa('promptTip2')}</li>
                <li>{pa('promptTip3')}</li>
                <li>{pa('promptTip4')}</li>
              </ul>
            }
            type="info"
          />
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
          {isDirty && (
            <Tag color="orange">{pa('unsavedChanges')}</Tag>
          )}

          {config && (
            <button
              className="btn-secondary"
              onClick={handleCreateNewVersion}
              disabled={createMutation.isPending}
            >
              <PlusOutlined style={{ marginRight: 4 }} />
              {pa('createNewVersion')}
            </button>
          )}

          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={!isDirty || updateMutation.isPending || createMutation.isPending}
          >
            <SaveOutlined style={{ marginRight: 4 }} />
            {updateMutation.isPending || createMutation.isPending
              ? t('loading', { ns: 'tables' })
              : config ? pa('saveChanges') : pa('createConfiguration')}
          </button>
        </div>
      </Form>

      {/* New Version Options Modal */}
      <Modal
        title={pa('createNewVersionOptions') || 'Create New Version - Options'}
        open={newVersionModalVisible}
        onCancel={() => setNewVersionModalVisible(false)}
        onOk={handleConfirmNewVersion}
        confirmLoading={createMutation.isPending}
        okText={pa('create') || 'Create'}
        cancelText={pa('cancel') || 'Cancel'}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Paragraph>
              {pa('newVersionDescription') || 'Choose what to copy from the current configuration:'}
            </Paragraph>
          </div>

          <Checkbox
            checked={copyModelSettings}
            onChange={(e) => setCopyModelSettings(e.target.checked)}
          >
            <Space direction="vertical" size={0}>
              <Text strong>{pa('copyModelSettings') || 'Copy Model Settings'}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {pa('copyModelSettingsDesc') || 'Model version, temperature, max tokens, timeout'}
              </Text>
            </Space>
          </Checkbox>

          <Checkbox
            checked={copyReferenceFiles}
            onChange={(e) => setCopyReferenceFiles(e.target.checked)}
          >
            <Space direction="vertical" size={0}>
              <Text strong>{pa('copyReferenceFiles') || 'Copy Reference Files (Knowledge Base)'}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {pa('copyReferenceFilesDesc') || `${config?.referenceFiles?.length || 0} file(s) will be copied`}
              </Text>
            </Space>
          </Checkbox>

          <Alert
            message={
              copyReferenceFiles
                ? pa('willUseCurrentPrompt') || 'Will use the current prompt from the editor (including any unsaved changes)'
                : pa('willCreateWithCurrentSettings') || 'Will create new version with current editor content'
            }
            type="info"
            showIcon
          />
        </Space>
      </Modal>
    </div>
  );
}
